import { enhancedApi as api } from './generated/documentGenerationService';

/**
 * Ensure that we are using FormData for multipart/form-data requests
 * Issue: https://github.com/reduxjs/redux-toolkit/issues/3063
 */
export const enhancedApi = api.enhanceEndpoints({
  endpoints: {
    // Template: PDF
    pdfTemplatesCreate: {
      query: (queryArg) => {
        const formData = new FormData();

        Object.entries(queryArg.createPdfTemplateSchema).forEach(
          ([key, value]) => {
            if (value) {
              formData.append(key, value);
            }
          },
        );

        return {
          url: `/api/document-generation/pdf/templates/`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: [{ type: 'pdf_templates' as const, id: 'LIST' }],
    },
    pdfTemplatesRetrieve: {
      providesTags: (result, error, { templateId }) => [
        { type: 'pdf_templates' as const, id: templateId },
      ],
    },
    pdfTemplatesUpdate: {
      invalidatesTags: (result, error, { templateId }) => [
        { type: 'pdf_templates' as const, id: templateId },
      ],
    },
    pdfTemplatesDestroy: {
      invalidatesTags: (result, error, { templateId }) => [
        { type: 'pdf_templates' as const, id: templateId },
        { type: 'pdf_templates' as const, id: 'LIST' },
      ],
    },
    pdfTemplateImagesCreate: {
      query: (queryArg) => {
        const formData = new FormData();
        Object.entries(queryArg.createPdfTemplateImageSchema).forEach(
          ([key, value]) => {
            if (value) {
              formData.append(key, value);
            }
          },
        );
        return {
          url: `/api/document-generation/pdf/templates_images/`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: [{ type: 'pdf_template_images' as const, id: 'LIST' }],
    },
    pdfTemplateImagesRetrieve: {
      providesTags: (result) =>
        result ? [{ type: 'pdf_template_images' as const, id: result.id }] : [],
    },
    // Template: DOCX
    docxTemplatesCreate: {
      query: (queryArg) => {
        const formData = new FormData();
        Object.entries(queryArg.createDocxTemplateSchema).forEach(
          ([key, value]) => {
            if (value) {
              formData.append(key, value);
            }
          },
        );
        return {
          url: `/api/document-generation/docx/templates/`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['docx_templates'],
    },
    docxTemplatesRetrieve: {
      providesTags: (result, error, { templateId }) => [
        { type: 'docx_templates', id: templateId },
      ],
    },
    docxTemplatesUpdate: {
      invalidatesTags: (result, error, { templateId }) => [
        { type: 'docx_templates', id: templateId },
      ],
    },
    docxTemplatesDestroy: {
      invalidatesTags: (result, error, { templateId }) => [
        { type: 'docx_templates', id: templateId },
      ],
    },
    pdfTemplatesNormalizePdf: {
      query: (queryArg) => {
        const formData = new FormData();
        formData.append('pdf', queryArg.normalizePdfSchema.pdf);
        return {
          url: `/api/document-generation/pdf/templates/normalize_pdf/`,
          method: 'POST',
          body: formData,
          responseHandler: (response) => response.blob(),
          cache: 'no-store',
        };
      },
    },
    // Extract / Render helpers
    docxTemplatesExtractFields: {
      query: (queryArg) => {
        const formData = new FormData();
        formData.append('docx', queryArg.extractDocxFieldsSchema.docx);
        return {
          url: `/api/document-generation/docx/templates/extract_fields/`,
          method: 'POST',
          body: formData,
        };
      },
    },
    docxTemplatesRender: {
      query: (queryArg) => {
        const formData = new FormData();
        Object.entries(queryArg.renderDocxTemplateSchema).forEach(
          ([key, value]) => {
            if (value && !(value instanceof Blob)) {
              formData.append(key, JSON.stringify(value));
            } else if (value instanceof Blob) {
              formData.append(key, value);
            }
          },
        );
        return {
          url: `/api/document-generation/docx/templates/render/`,
          method: 'POST',
          body: formData,
          cache: 'no-store',
          responseHandler: (response) => response.blob(),
        };
      },
    },
    docxTemplatesRenderToPdf: {
      query: (queryArg) => {
        const formData = new FormData();
        formData.append('docx', queryArg.renderDocxToPdfSchema.docx);
        return {
          url: `/api/document-generation/docx/templates/render_to_pdf/`,
          method: 'POST',
          body: formData,
          cache: 'no-store',
          responseHandler: (response) => response.blob(),
        };
      },
    },

    // Fields: PDF
    pdfTemplateFieldsList: {
      providesTags: (result, error, { templateId }) => [
        { type: 'pdf_template_fields', id: templateId },
      ],
    },
    pdfTemplateFieldsCreate: {
      invalidatesTags: (result, error, { templateId }) => [
        { type: 'pdf_template_fields', id: templateId },
      ],
    },
    pdfTemplateFieldsRetrieve: {
      providesTags: (result) => [
        { type: 'pdf_template_fields', id: result?.id },
      ],
    },
    pdfTemplateFieldsUpdate: {
      invalidatesTags: (result, error, { templateId, fieldId }) => [
        { type: 'pdf_template_fields', id: templateId },
        { type: 'pdf_template_fields', id: fieldId },
      ],
    },
    pdfTemplateFieldsDestroy: {
      invalidatesTags: (result, error, { templateId, fieldId }) => [
        { type: 'pdf_template_fields', id: templateId },
        { type: 'pdf_template_fields', id: fieldId },
      ],
    },
    pdfTemplateReplaceFields: {
      invalidatesTags: (result, error, { templateId }) => [
        { type: 'pdf_template_fields', id: templateId },
        { type: 'pdf_templates', id: templateId },
      ],
    },

    // Fields: DOCX
    docxTemplateFieldsList: {
      providesTags: (result, error, { templateId }) => [
        { type: 'docx_template_fields', id: templateId },
      ],
    },
    docxTemplateFieldsCreate: {
      invalidatesTags: (result, error, { templateId }) => [
        { type: 'docx_template_fields', id: templateId },
      ],
    },
    docxTemplateFieldsUpdate: {
      invalidatesTags: (result, error, { templateId, fieldId }) => [
        { type: 'docx_template_fields', id: templateId },
        { type: 'docx_template_fields', id: fieldId },
      ],
    },
    docxTemplateFieldsDestroy: {
      invalidatesTags: (result, error, { templateId, fieldId }) => [
        { type: 'docx_template_fields', id: templateId },
        { type: 'docx_template_fields', id: fieldId },
      ],
    },
    docxTemplatesReplaceFields: {
      invalidatesTags: (result, error, { templateId }) => [
        { type: 'docx_template_fields', id: templateId },
        { type: 'docx_templates', id: templateId },
      ],
    },

    // Document Generation Template (meta)
    documentGenerationTemplatesRetrieve: {
      providesTags: (result) => [
        { type: 'document_generation_templates', id: result?.id },
      ],
    },
  },
});

export const {
  usePdfTemplatesCreateMutation,
  usePdfTemplatesUpdateMutation,
  usePdfTemplateReplaceFieldsMutation,
  usePdfTemplatesDestroyMutation,
  usePdfTemplatesRetrieveQuery,
  usePdfTemplateImagesCreateMutation,
  useDocxTemplatesCreateMutation,
  useDocxTemplatesUpdateMutation,
  useDocxTemplatesDestroyMutation,
  useDocxTemplatesRetrieveQuery,
  useDocxTemplatesReplaceFieldsMutation,
  useDocumentGenerationTemplatesRetrieveQuery,
} = enhancedApi;
